/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Else, If, Then, When } from "react-if";
import { FlexContainer, Tag } from "@nordcloud/gnui";
import { useToggle } from "~/hooks";
import { ACTIONS, Can, useAccess } from "~/services/auth";
import { useBillingPeriod } from "~/services/customers";
import { isEmpty } from "~/tools";
import { OrgUnitTags } from "./OrgUnitTags";
import { EnvironmentUnitsListProps } from "./types";

export function EnvironmentUnitsList({
  orgUnits,
  setIsOpen,
  setIsModalConfirmOrgRemoveOpen,
  setRemoveOrgUnit,
}: EnvironmentUnitsListProps) {
  const [isEditMode, toggleEditMode] = useToggle(false);

  const { hasAccess } = useAccess(ACTIONS.addOrgUnit);
  const { isCurrentBillingPeriod } = useBillingPeriod();

  const readOnly = !hasAccess;

  return (
    <If condition={isEditMode}>
      <Then>
        <FlexContainer alignItems="center" wrap="wrap">
          {orgUnits.map((orgUnit) => {
            return (
              <Tag
                key={orgUnit.nid}
                showClose
                text={orgUnit.name}
                color={orgUnit.businessContext?.color ?? undefined}
                onClick={() => {
                  setRemoveOrgUnit({
                    nid: orgUnit.nid || "",
                    name: orgUnit.name || "",
                  });
                  setIsModalConfirmOrgRemoveOpen(true);
                }}
              />
            );
          })}
          <Tag text="Add" icon="plus" onClick={() => setIsOpen(true)} />
          <Tag text="Close" icon="close" onClick={toggleEditMode} />
        </FlexContainer>
      </Then>
      <Else>
        <FlexContainer alignItems="center" wrap="wrap">
          <OrgUnitTags orgUnits={orgUnits} readOnly={readOnly} />
          <Can action={ACTIONS.addOrgUnit}>
            <When condition={isCurrentBillingPeriod}>
              <If condition={isEmpty(orgUnits)}>
                <Then>
                  <Tag text="Add" icon="plus" onClick={() => setIsOpen(true)} />
                </Then>
                <Else>
                  <Tag
                    icon="edit"
                    data-testid="Edit"
                    onClick={toggleEditMode}
                  />
                </Else>
              </If>
            </When>
          </Can>
        </FlexContainer>
      </Else>
    </If>
  );
}
